import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, map, Observable, of } from "rxjs";
import { API_CONSTANTS } from "src/app/static/api.constant";
import { DataService } from "../data/data.service";
import { RequestParam } from "src/app/interfaces/requestParam";
import { defaultDatonProduct, defaultIqProduct, defaultPulseProduct, productButtons, productChips } from "src/app/static/products";

@Injectable({
    providedIn: 'root',
})
export class ProductDetailService {
    constructor(private dataService: DataService) { }

    getProductDetails(companyId: number): Observable<{ data?: any; success: boolean; error?: any }> {
        const requestParam: RequestParam = {
            url: API_CONSTANTS.PRODUCT_DETAIL(companyId)
        };
        return this.dataService.get(requestParam).pipe(
            map((response: any) => {
                if(response){
                    return {data: this.generateProductInfo(response), success: true}
                }
                return { error: response, success: false, errors: this.dataService.getError(response) };
            }),
            catchError((error) => {
                console.error('Error fetching product details:', error);
                return of({ error, success: false, errors: this.dataService.getError(error) });
            })
        );
    }

    generateProductInfo(response: any){
        let products: IProducts = {daton: null, pulse: null, iq: null};
        const dataArray = response?.data || response;
        
        if(dataArray && !!dataArray.length){
            dataArray.forEach((res: any)=>{
                const productAction = this.getProductActionInfo(res);
                const product: IProductInfo = {
                    name: res.subscriptionPlan.product.productName,
                    id: res.subscriptionPlan.product.id,
                    remainingDays: 0,
                    status: res.status,
                    chip: productAction.chip,
                    button: productAction.button
                };
                if(product.name?.toLowerCase() === 'daton'){
                    products.daton = product;
                }else if(product.name?.toLowerCase() === 'insights' || product.name?.toLowerCase() === 'pulse'){
                    products.pulse = product;
                }else if(product.name?.toLowerCase() === 'iq'){
                    products.iq = product;
                }
            })
        }
        if(!products.daton){
            products.daton = defaultDatonProduct
        }
        if(!products.pulse){
            products.pulse = defaultPulseProduct
        }
        if(!products.iq){
            products.iq = defaultIqProduct;
        }
        console.log('Generated product info', products);
        return products;
    }

    getProductLogo(product: string): string {
    const productValue = product?.toUpperCase();
    if (productValue === 'PULSE' || productValue === 'INSIGHTS') {
      return '/assets/pulse_logo.svg';
    } else if (productValue === 'DATON') {
      return '/assets/daton_logo.svg';
    } else if (productValue === 'IQ') {
      return '/assets/sarasiq_logo.svg';
    } else {
      return '/assets/saras_blue.svg';
    }
  }

    getProductActionInfo(response: any): {chip: IProductChip, button: IProductButton}{
        const product: IProductInfo = {
            name: response.subscriptionPlan.product.productName,
            id: response.subscriptionPlan.product.id,
            remainingDays: 0,
            status: response.status,
            chip: productChips.trialNotStarted,
            button: productButtons.startFreeTrial
        };
        let chip: IProductChip = productChips.trialNotStarted;
        let button: IProductButton = product.name?.toLowerCase() === 'daton'? productButtons.startFreeTrial : productButtons.scheduleDemo;
        switch (response.status) {
            case 'ACTIVE':
            case 'UPGRADED':
            case 'DOWNGRADED':
                chip = productChips.activeSubscription,
                button = productButtons.goToProduct
                break;
            case 'CANCELLED':
            case 'SUSPENDED':
                chip = productChips.cancledSubscription, 
                button = productButtons.viewPricing
                break;
            case 'CANCELLATION_REQUESTED':
                chip = productChips.cancellationRequest, 
                button = productButtons.goToProduct
                break;
            case 'TRIAL':
                if(response.nextBillingDate){
                    const daysRemaining = Math.floor((response.nextBillingDate - Date.now())/(1000 * 60 * 60 * 24));
                    if(!!daysRemaining){
                        if(daysRemaining > 7){
                            chip = productChips.trialStarted,
                            button = productButtons.goToProduct
                        }else{
                            chip = productChips.trialEnding(daysRemaining),
                            button = productButtons.goToProduct
                        }
                    }else{
                        chip = productChips.trialEnded;
                        button = productButtons.viewPricing;
                    }
                }
                break;
            case 'TRIAL_ENDED':
                chip = productChips.trialEnded;
                button = productButtons.viewPricing;
                break;
            }
            return {chip, button}
    }

    validateProductSubscription(companyId: string, productId: string, productName: string){
        const requestParam: RequestParam = {
            url: API_CONSTANTS.VALIDATA_SUBSCRIPTION,
            params: {companyId, productId, productName}
        };
        return this.dataService.get(requestParam);
    }
}

export interface IProducts{
    daton: IProductInfo | null,
    pulse: IProductInfo | null,
    iq: IProductInfo | null
}
export interface IProductInfo{
    name: 'daton' | 'insights' | 'iq',
    id: number,
    remainingDays: number,
    status: 'TRIAL' | 'TRIAL_ENDED' | 'ACTIVE' | 'UPGRADED' | 'DOWNGRADED' | 'CANCELLED' | 'NONE',
    chip: IProductChip,
    button: IProductButton,
    nextBillingDate?: number,
    overageBillingEndDate?: number,
    billingDays?: number,
    billingType?: number,
    isTrialEntryEmpty?: boolean,
    showSpinner?: boolean
}
export interface IProductChip{
    text: string,
    state: 'neutral' | 'primary' | 'error' | 'warning' | 'success'
    id: string,
    type: string,
    filling: string,
    className?: string
}
export interface IProductButton{
    text: string,
    type: 'START_FREE_TRIAL' | 'GO_TO_PRODUCT' | 'VIEW_PRICING' | 'SCHEDULE_DEMO'
}
export enum ProductActionEnum {
    START_FREE_TRIAL='START_FREE_TRIAL',
    GO_TO_PRODUCT='GO_TO_PRODUCT',
    VIEW_PRICING='VIEW_PRICING',
    SCHEDULE_DEMO='SCHEDULE_DEMO'
}